<template>
  <div>
  <div v-show="contentMenu" class="editor-context-menu" :style="{ left: x + 'px', top: y + 'px' }" role="menu" aria-label="画布右键菜单" ref="menu" @contextmenu.prevent @keydown="moveFocus">
    <template v-if="!blank">
      <button type="button" role="menuitem" @click="handleLock"><i aria-hidden="true" :class="allLocked ? 'el-icon-unlock' : 'el-icon-lock'"></i>{{ allLocked ? '解锁' : '锁定' }}<small>Ctrl + Shift + L</small></button>
      <button type="button" role="menuitem" :disabled="!editable" @click="handleDel"><i aria-hidden="true" class="el-icon-delete"></i>删除<small>Delete</small></button>
      <div class="editor-context-layers" :class="{ 'opens-left': opensLeft }" @mouseenter="layersOpen = true" @mouseleave="layersOpen = false" @focusin="layersOpen = true" @focusout="leaveLayers">
        <button type="button" role="menuitem" aria-haspopup="menu" :aria-expanded="String(layersOpen)" :disabled="!singleEditable" @click="layersOpen = !layersOpen"><i aria-hidden="true" class="el-icon-sort"></i>图层顺序<i aria-hidden="true" class="el-icon-caret-right editor-context-caret"></i></button>
        <div v-show="layersOpen && singleEditable" class="editor-context-submenu" role="menu" aria-label="图层顺序">
          <button v-for="order in orders" :key="order.key" type="button" role="menuitem" @click="handleOrder(order.key)"><i aria-hidden="true" :class="order.icon"></i>{{ order.label }}<small>{{ order.shortcut }}</small></button>
        </div>
      </div>
      <button type="button" role="menuitem" @click="handleCopy"><i aria-hidden="true" class="el-icon-copy-document"></i>复制<small>Ctrl + C</small></button>
    </template>
    <button type="button" role="menuitem" :disabled="!clipboard.length" @click="handlePaste(true)"><i aria-hidden="true" class="el-icon-document-copy"></i>粘贴<small>Ctrl + V</small></button>
    <template v-if="!blank">
      <button type="button" role="menuitem" :disabled="!editable" @click="handleCut"><i aria-hidden="true" class="el-icon-scissors"></i>剪切<small>Ctrl + X</small></button>
      <button v-if="contain.isFolder" type="button" role="menuitem" :disabled="!canUngroup" @click="handleLogout"><i aria-hidden="true" class="el-icon-folder-opened"></i>解散文件组</button>
      <button v-else-if="contain.active.length > 1" type="button" role="menuitem" :disabled="!canCompose" @click="handleCompose"><i aria-hidden="true" class="el-icon-folder-add"></i>新建文件组</button>
      <button type="button" role="menuitem" :disabled="!canSaveTemplate || !roots.length" @click="saveAsTemplate"><i aria-hidden="true" class="el-icon-star-off"></i>存为模板</button>
    </template>
  </div>
  <template-save-dialog v-if="templateVisible" :visible.sync="templateVisible" :snapshot="templateSnapshot" :default-name="templateName" @saved="onTemplateSaved" />
  </div>
</template>

<script>
import { uuid } from '@/utils/utils'
import { selectedRoots, canEditNode, removeNodes, reorderNode, cloneNodes, isTextEditing } from '@/editor/core/node-operations'
import { limitNodeBounds } from '@/editor/core/component-size'
import { selectionMovePlan, selectionMoveDelta, isSelectionMoveKey } from '@/editor/core/selection-drag'
import { screenToCanvas } from '@/editor/core/viewport'
import { createTemplateSnapshot } from '@/editor/core/component-template'
import TemplateSaveDialog from '@/components/Templates/save-dialog.vue'
import store from '@/store'
export default {
  components: { TemplateSaveDialog },
  inject: ['contain'],
  data: () => ({
    templateVisible: false, templateSnapshot: null, templateName: '',
    contentMenu: false, blank: false, x: 0, y: 0, layersOpen: false, opensLeft: false, clipboard: [], pasteCount: 0,
    orders: [
      { key: 'up', label: '上移', icon: 'el-icon-top', shortcut: 'Ctrl + Alt + ↑' },
      { key: 'down', label: '下移', icon: 'el-icon-bottom', shortcut: 'Ctrl + Alt + ↓' },
      { key: 'top', label: '置顶', icon: 'el-icon-upload2', shortcut: 'Alt + Shift + ↑' },
      { key: 'bottom', label: '置底', icon: 'el-icon-download', shortcut: 'Alt + Shift + ↓' }
    ]
  }),
  computed: {
    canSaveTemplate () { return store.getters['auth/hasResourcePermission']('visualizationTemplate:save') && store.getters['auth/hasResourcePermission']('visualizationTemplate:query') },
    structureReady () { return !!this.contain.projectRecord && this.contain.editorSaveReady !== false && !this.contain.editorVersionSwitching },
    roots () { return selectedRoots(this.contain.nav, this.contain.active) },
    canCompose () { return this.structureReady && this.roots.length > 0 && this.roots.every(canEditNode) && this.roots.every(item => item.siblings === this.roots[0].siblings) },
    canUngroup () { return this.structureReady && this.roots.length === 1 && !!this.roots[0].node.children && canEditNode(this.roots[0]) },
    allLocked () { return this.roots.length > 0 && this.roots.every(item => item.node.locked) },
    editable () { return this.roots.some(canEditNode) },
    singleEditable () { return this.roots.length === 1 && canEditNode(this.roots[0]) }
  },
  mounted () {
    document.addEventListener('keydown', this.handleKeyDown, true);
    document.addEventListener('mousedown', this.closeOutside);
    window.addEventListener('blur', this.hide);
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.handleKeyDown, true);
    document.removeEventListener('mousedown', this.closeOutside);
    window.removeEventListener('blur', this.hide);
    if (this.feedback) this.feedback.close();
  },
  methods: {
    saveAsTemplate () {
      if (!this.canSaveTemplate || !this.roots.length) return;
      try {
        const selected = new Set(this.roots.map(item => item.node));
        const ordered = [];
        const collect = nodes => nodes.forEach(node => {
          if (selected.has(node)) ordered.push(node);
          else if (node.children) collect(node.children);
        });
        collect(this.contain.nav);
        this.templateSnapshot = createTemplateSnapshot(ordered);
        this.templateName = this.roots.length === 1 ? this.roots[0].node.name || this.roots[0].node.title || '' : '组件组合';
        this.hide(); this.templateVisible = true;
      } catch (error) { this.notify(error.message, 'warning'); this.hide(); }
    },
    onTemplateSaved () {
      this.notify('组件模板已保存，可在资源中心 → 模板 → 组件模板中管理', 'success');
      if (this.contain.$refs.palette && this.contain.$refs.palette.refreshTemplates) this.contain.$refs.palette.refreshTemplates();
    },
    notify (message, type = 'info') {
      if (this.feedback) this.feedback.close();
      // 仅提示操作结果，不拦截画布和工具栏，也不因鼠标悬停暂停消失。
      this.feedback = this.$message({ message, type, duration: 2400, customClass: 'editor-feedback-message' });
    },
    show (x, y, blank = false) {
      this.blank = blank;
      this.layersOpen = false;
      this.contentMenu = true;
      this.previousFocus = document.activeElement;
      const rect = this.contain.$refs.container.$refs.container.getBoundingClientRect();
      this.pastePoint = screenToCanvas({ x, y }, { left: rect.left, top: rect.top, zoom: this.contain.editorViewport.zoom });
      this.$nextTick(() => {
        const menu = this.$refs.menu;
        this.x = Math.max(4, Math.min(x, window.innerWidth - menu.offsetWidth - 4));
        this.y = Math.max(4, Math.min(y, window.innerHeight - menu.offsetHeight - 4));
        this.opensLeft = this.x + menu.offsetWidth + 220 > window.innerWidth;
        const button = menu.querySelector('button:not(:disabled)');
        if (button) button.focus();
      });
    },
    hide () { this.contentMenu = false; this.layersOpen = false; },
    closeOutside (event) { if (!this.$refs.menu.contains(event.target)) this.hide(); },
    leaveLayers (event) { if (!event.currentTarget.contains(event.relatedTarget)) this.layersOpen = false; },
    moveFocus (event) {
      if (event.key === 'Escape') {
        this.hide();
        if (this.previousFocus && this.previousFocus.isConnected) this.previousFocus.focus();
      }
      if (!['ArrowUp', 'ArrowDown'].includes(event.key) || event.ctrlKey || event.altKey) return;
      event.preventDefault();
      const buttons = Array.from(this.$el.querySelectorAll('button:not(:disabled)')).filter(button => button.offsetParent !== null);
      const index = buttons.indexOf(document.activeElement);
      if (buttons.length) buttons[(index + (event.key === 'ArrowDown' ? 1 : buttons.length - 1)) % buttons.length].focus();
    },
    handleKeyDown (event) {
      if (event.target && event.target.closest && event.target.closest('[data-editor-guides]')) return;
      if (isTextEditing(event.target) || !this.contain.projectRecord) return;
      if (!this.contentMenu && isSelectionMoveKey(event) && (this.roots.length > 1 || this.roots[0] && this.roots[0].node.children)) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.moveSelectedByKeyboard(event);
        return;
      }
      const ctrl = event.ctrlKey || event.metaKey;
      const key = event.key.toLowerCase();
      let action;
      if (key === 'delete' && this.contain.active.length) action = this.handleDel;
      else if (ctrl && key === 'c' && this.contain.active.length) action = this.handleCopy;
      else if (ctrl && key === 'x' && this.editable) action = this.handleCut;
      else if (ctrl && key === 'v' && this.clipboard.length) action = () => this.handlePaste(false);
      else if (ctrl && event.shiftKey && key === 'l' && this.contain.active.length) action = this.handleLock;
      else if (event.altKey && ['arrowup', 'arrowdown'].includes(key) && this.singleEditable) {
        const order = event.shiftKey ? (key === 'arrowup' ? 'top' : 'bottom') : ctrl ? (key === 'arrowup' ? 'up' : 'down') : null;
        if (order) action = () => this.handleOrder(order);
      }
      if (action) { event.preventDefault(); event.stopPropagation(); action(); }
    },
    moveSelectedByKeyboard (event) {
      const stage = this.contain.$refs.container;
      if (!this.structureReady || !stage || !stage.isEditing || stage.panning || stage.gradeFlag || stage.loadingData || stage.loadError) return;
      const anchor = this.roots.find(canEditNode);
      const plan = anchor && selectionMovePlan(this.contain.nav, this.contain.active, anchor.node.index);
      if (!plan || !plan.entries.length) return;
      const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      const direction = directions[event.key];
      if (!direction) return;
      const step = Math.round((Number(stage.stepScale) || 1) * 100) / 100;
      const delta = selectionMoveDelta(plan, { x: direction[0] * step, y: direction[1] * step }, this.contain.config);
      if (!delta.x && !delta.y) return;
      this.contain.rememberStructure();
      plan.entries.forEach(entry => {
        const left = entry.left + delta.x, top = entry.top + delta.y;
        this.$set(entry.node, 'left', left); this.$set(entry.node, 'top', top);
        const subgroup = stage.$refs && stage.$refs.subgroup;
        const refs = subgroup && subgroup.handleGetObj(entry.node.index);
        const frame = Array.isArray(refs) ? refs[0] : refs;
        if (frame && !frame._isDestroyed) { frame.setLeft(left); frame.setTop(top); }
      });
      this.contain.rememberStructure();
    },
    handleLock () {
      this.contain.rememberStructure();
      const locked = !this.allLocked;
      this.roots.forEach(item => {
        if (!item.ancestors.some(parent => parent.locked)) this.$set(item.node, 'locked', locked);
        else this.notify('请先解锁父级分组');
      });
      this.hide();
    },
    handleDel () {
      this.contain.rememberStructure();
      const removed = removeNodes(this.contain.nav, this.contain.active);
      if (removed.length) {
        this.contain.handleInitActive();
        this.notify('已删除，可按 Ctrl + Z 撤销', 'success');
      } else this.notify('锁定的组件或分组不能删除，请先解锁');
      this.hide();
    },
    handleCopy () {
      this.clipboard = JSON.parse(JSON.stringify(this.roots.map(item => item.node)));
      this.pasteCount = 0;
      this.hide();
    },
    handleCut () {
      this.clipboard = JSON.parse(JSON.stringify(this.roots.filter(canEditNode).map(item => item.node)));
      this.pasteCount = 0;
      if (this.clipboard.length) this.handleDel();
    },
    handlePaste (atPointer = false) {
      if (!this.clipboard.length) return;
      this.contain.rememberStructure();
      this.pasteCount++;
      let offset = { x: 20 * this.pasteCount, y: 20 * this.pasteCount };
      if (atPointer) {
        const leaves = [];
        const collect = nodes => nodes.forEach(node => node.children ? collect(node.children) : leaves.push(node));
        collect(this.clipboard);
        if (leaves.length) offset = { x: this.pastePoint.x - Math.min(...leaves.map(node => Number(node.left || 0))), y: this.pastePoint.y - Math.min(...leaves.map(node => Number(node.top || 0))) };
      }
      const nodes = cloneNodes(this.clipboard, uuid, offset);
      limitNodeBounds(nodes, this.contain.config);
      this.contain.nav.unshift(...nodes);
      this.contain.active = nodes.map(node => node.index);
      this.hide();
    },
    handleOrder (action) {
      if (!this.singleEditable) return;
      this.contain.rememberStructure();
      reorderNode(this.contain.nav, this.contain.active[0], action);
      this.hide();
    },
    handleCompose () {
      const roots = this.roots;
      if (!this.structureReady || !roots.length || !roots.every(canEditNode)) return;
      if (!roots.every(item => item.siblings === roots[0].siblings)) { this.notify('请选择同一层级的图层再创建文件组'); return; }
      this.contain.rememberStructure();
      roots.sort((a, b) => a.index - b.index);
      const siblings = roots[0].siblings;
      const index = roots[0].index;
      const names = new Set();
      const collectNames = nodes => nodes.forEach(node => { names.add(node.name); if (node.children) collectNames(node.children); });
      collectNames(this.contain.nav);
      roots.forEach(item => siblings.splice(siblings.indexOf(item.node), 1));
      let number = 1;
      while (names.has('文件组 ' + number)) number++;
      const group = { index: uuid(), name: '文件组 ' + number, title: '文件组', menu: true, children: roots.map(item => item.node) };
      siblings.splice(index, 0, group);
      this.contain.active = [group.index];
      this.contain.rememberStructure();
      this.hide();
    },
    handleLogout () {
      const item = this.roots[0];
      if (!this.structureReady || this.roots.length !== 1 || !canEditNode(item) || !item.node.children) return;
      this.contain.rememberStructure();
      item.siblings.splice(item.index, 1, ...item.node.children);
      this.contain.handleInitActive();
      this.contain.rememberStructure();
      this.hide();
    }
  }
}
</script>
